import { computed } from 'vue';
import { useSignStore } from '@/stores/sign.ts';
import { useRoute } from 'vue-router';
import { useCreateStore } from '@/stores/create.ts';
import {
  findSigneeGroupForSignee,
  isSigneeGroupQuorumSet
} from '@/utils/signeeSignings.ts';

export function useDocumentContext() {
  const signStore = useSignStore();
  const createStore = useCreateStore();

  const route = useRoute();

  const isDraft = computed(() => {
    return (
      route.name === 'document-create' || route.name === 'p-document-create'
    );
  });

  const currentDocument = computed(() => {
    if (isDraft.value || isDocumentAuthRoute.value) {
      return createStore.document;
    } else {
      return signStore.document;
    }
  });

  const currentPlacedSignatures = computed(() => {
    if (isDraft.value || isDocumentAuthRoute.value) {
      return createStore.placedSignatures;
    } else {
      return signStore.placedSignatures;
    }
  });

  // The signees every consumer OUTSIDE the signing-workflow dialog renders from
  // — the PDF canvas' placeholder rebuild, PlaceholderWrapper and the sidebar.
  // Deliberately the COMMITTED list (`committedSignees`), not the draft-aware
  // `filteredSignees`: while the dialog is open its edits live in a draft, so
  // reading the draft here would rebuild placeholders behind the dialog for
  // participants the user has not confirmed yet (and drop them again on
  // Cancel). Reconcile commits the draft into the document on Confirm, which is
  // what makes the new participants show up here. Outside the dialog the two
  // lists are the same data.
  const currentSignees = computed(() => {
    if (isDraft.value || isDocumentAuthRoute.value) {
      return createStore.committedSignees;
    } else {
      return signStore.document?.signees;
    }
  });

  const isDocumentAuthRoute = computed(() => {
    return route.name?.startsWith('p-document') ?? false;
  });

  const isSignRoute = computed(
    () =>
      route.name === 'document-sign' ||
      route.name === 'p-document-sign' ||
      route.name === 'sign-id'
  );

  // Whether a placeholder placement change made on the sign step (resize,
  // delete, auto-place or drag/move to another page) must be persisted to the
  // signee's `fileAutographPositions` via the sign store, mirroring what the
  // draft step already does through `createStore.updateInvitee`. Envelopes
  // (multi-file documents) need every file's position saved ahead of time
  // because the final sign request can only ever carry a single
  // `autographPosition` for the whole document. This must hold even when
  // `canModifyAutographPosition` is false at the document/signee level, since
  // a placeholder can still be editable through a per-file signing policy
  // (see `effectivePolicy` in `usePlacement.ts`). Never true for a quorum-set
  // signee group (`isSigneeGroupQuorumSet`): its shared slot is always
  // persisted via the signee-group endpoint instead, draft-only, regardless
  // of the sign-step/envelope case.
  const shouldPersistSignPlacement = computed(() => {
    if (!isSignRoute.value || !signStore.isEnvelope || !signStore.isSignee) {
      return false;
    }
    const signeeId = signStore.document?.signContext?.signeeId;
    if (!signeeId) return false;
    return !isSigneeGroupQuorumSet(
      findSigneeGroupForSignee(signeeId, signStore.document?.signeeGroups)
    );
  });

  return {
    isDocumentAuthRoute,
    isDraft,
    isSignRoute,
    shouldPersistSignPlacement,
    currentDocument,
    currentPlacedSignatures,
    currentSignees,
    route
  };
}
